import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { CSSObject, styled } from '../theming';
import { fallbackTheme } from '../theming/fallbackTheme';
import { TagColor, TagColorNew, TagColorLegacy } from './types';

// temporary mapping of legacy color values to the new ones until all clients have migrated
// TODO: remove once all clients finished migrating to the new values or we created a codemod.
const colorLegacyToNewMapping: Record<TagColorLegacy, TagColorNew> = {
  information: 'default',
  important: 'default',
  locked: 'default',
  warning: 'warning',
  available: 'default',
  unavailable: 'default',
  premium: 'default',
};

const getColorSemanticTokens = (color: TagColor, theme: BabbelTheme) => {
  let tagColor;

  // temporary mapping of legacy color values to the new ones until all clients have migrated
  // TODO: remove once all clients finished migrating to the new values or we created a codemod.
  if (Object.keys(colorLegacyToNewMapping).includes(color)) {
    // type-casting to let TS know we're certain this is a legacy color value
    const legacyColor = color as TagColorLegacy;
    tagColor = colorLegacyToNewMapping[legacyColor];
  } else {
    tagColor = color;
  }

  const colorSemanticTokens = { background: '', foreground: '' };

  switch (tagColor) {
    case 'default-lightened':
      colorSemanticTokens.background = fallbackTheme(theme).color.neutral.surfaceLightened;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.neutral.onSurfaceDefault;
      break;
    case 'default-inverse':
      colorSemanticTokens.background = fallbackTheme(theme).color.neutral.surfaceInverseDefault;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.neutral.onSurfaceInverse;
      break;
    case 'translucent':
      colorSemanticTokens.background = fallbackTheme(theme).color.hidden.translucentLight;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.illustration.onSurfaceDark;
      break;
    case 'translucent-dark':
      colorSemanticTokens.background = fallbackTheme(theme).color.hidden.translucentDark;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.illustration.onSurfaceLight;
      break;
    case 'success':
      colorSemanticTokens.background = fallbackTheme(theme).color.feedback.success.surface;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.feedback.success.onSurface;
      break;
    case 'warning':
      colorSemanticTokens.background = fallbackTheme(theme).color.feedback.warning.surface;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.feedback.warning.onSurface;
      break;
    case 'failure':
      colorSemanticTokens.background = fallbackTheme(theme).color.feedback.attention.surface;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.feedback.attention.onSurface;
      break;
    default:
      colorSemanticTokens.background = fallbackTheme(theme).color.neutral.surfaceDarkened1;
      colorSemanticTokens.foreground = fallbackTheme(theme).color.neutral.onSurfaceDefault;
  }

  return colorSemanticTokens;
};

const tokenToCSS = (color: TagColor, theme: BabbelTheme): CSSObject => {
  const colorSemanticTokens = getColorSemanticTokens(color, theme);

  return {
    backgroundColor: colorSemanticTokens.background,
    color: colorSemanticTokens.foreground,
  };
};

export const StyledTag = styled.span<{
  color: TagColor;
}>`
  display: flex;
  align-items: center;
  width: auto;
  border-radius: ${tokens.size.space.xxxsmall};
  height: ${tokens.size.space.medium};
  padding: 2px ${tokens.size.space.xxsmall};
  ${({ theme, color }): CSSObject => ({
    ...(color && tokenToCSS(color, theme)),
  })}
`;

export const StyledIconWrapper = styled.span<{ alignIcon: 'start' | 'end' }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding-top: ${tokens.size.space.xxxsmall};
  padding-bottom: ${tokens.size.space.xxxsmall};
  ${({ alignIcon }) =>
    alignIcon === 'end'
      ? `margin-left: ${tokens.size.space.xxxsmall}`
      : `margin-right: ${tokens.size.space.xxxsmall}`};
`;
